import classNames from 'classnames'
import { Lock, TriangleAlert } from 'lucide-react'
import { useIntl } from 'react-intl'
import { ConfirmationEventDetails } from 'src/modules/confirmation/components/confirmation-event-details'
import { Button, FormattedText, Icon, Typography } from 'src/modules/core/components'
import { resolvePolicyStepCopy } from './cancellation-policy-step.domain'
import type {
  CancellationPolicyStepProps,
  PolicyStepSectionProps,
  PolicyTextBlockProps,
} from './cancellation-policy-step.interface'
import classes from './cancellation-policy-step.module.scss'

function PolicyTextBlock({ text }: PolicyTextBlockProps) {
  if (!text) return null
  return (
    <div className={classes.policyBlock}>
      <Typography variant="span" className={classes.policyLabel}>
        <FormattedText id="confirmation.lateCancel.policyLabel" />
      </Typography>
      <Typography variant="body" className={classes.policyText}>
        {text}
      </Typography>
    </div>
  )
}

function WarningCallout({ copy }: Pick<PolicyStepSectionProps, 'copy'>) {
  const values = { business: copy.business, date: <strong>{copy.cutoffLabel}</strong> }
  return (
    <div className={classes.callout}>
      <TriangleAlert size={20} className={classes.calloutIcon} aria-hidden="true" />
      <Typography variant="body">
        <FormattedText
          id={
            copy.cutoffLabel
              ? 'confirmation.lateCancel.warning.callout'
              : 'confirmation.lateCancel.warning.calloutNoDate'
          }
          values={values}
        />
      </Typography>
    </div>
  )
}

function WarningStep({ event, copy, onKeep, onCancelAnyway, submittingAction }: PolicyStepSectionProps) {
  return (
    <div className={classes.root} data-testid="late-cancel-warning">
      <Typography variant="h1" className={classes.title}>
        <FormattedText id="confirmation.lateCancel.warning.title" />
      </Typography>
      <div className={classes.details}>
        <ConfirmationEventDetails event={event} />
      </div>
      <WarningCallout copy={copy} />
      <PolicyTextBlock text={copy.policyText} />
      <div className={classes.actions}>
        <Button onClick={onKeep} disabled={submittingAction !== null}>
          <FormattedText id="confirmation.lateCancel.warning.keep" />
        </Button>
        <Button
          variant="danger"
          onClick={onCancelAnyway}
          disabled={submittingAction !== null}
          isLoading={submittingAction === 'cancel'}
          loadingLabel={<FormattedText id="confirmation.button.cancelling" />}
        >
          <FormattedText id="confirmation.lateCancel.warning.cancelAnyway" />
        </Button>
      </div>
    </div>
  )
}

function StillComing({ event, onConfirm, submittingAction }: PolicyStepSectionProps) {
  return (
    <div className={classes.stillComing}>
      <Typography variant="body" className={classes.stillComingText}>
        <FormattedText id="confirmation.lateCancel.blocked.stillComing" />
      </Typography>
      {event.confirmed === 'confirmed' ? (
        <Typography variant="body" className={classes.alreadyConfirmed}>
          <FormattedText id="confirmation.alreadyConfirmed" />
        </Typography>
      ) : (
        <Button
          variant="withBorder"
          onClick={onConfirm}
          disabled={submittingAction !== null}
          isLoading={submittingAction === 'confirm'}
          loadingLabel={<FormattedText id="confirmation.button.confirming" />}
        >
          <FormattedText id="confirmation.button.confirm" />
        </Button>
      )}
    </div>
  )
}

function ContactCard({ copy }: Pick<PolicyStepSectionProps, 'copy'>) {
  return (
    <div className={classes.contactCard}>
      <Typography variant="body" className={classes.contactCardTitle}>
        {copy.business}
      </Typography>
      <Typography variant="body" className={classes.contactCardText}>
        <FormattedText id="confirmation.lateCancel.blocked.contactCard" values={{ business: copy.business }} />
      </Typography>
    </div>
  )
}

function BlockedStep(props: PolicyStepSectionProps) {
  const { event, copy } = props
  return (
    <div className={classes.root} data-testid="late-cancel-blocked">
      <div className={classNames(classes.badge, classes.lockBadge)}>
        <Lock size={22} aria-label="Locked" />
      </div>
      <Typography variant="h1" className={classes.title}>
        <FormattedText id="confirmation.lateCancel.blocked.title" />
      </Typography>
      <div className={classes.details}>
        <ConfirmationEventDetails event={event} />
      </div>
      <Typography variant="body" className={classes.bodyText}>
        <FormattedText id="confirmation.lateCancel.blocked.body" values={{ business: copy.business }} />
      </Typography>
      {copy.cutoffLabel && (
        <Typography variant="body" className={classes.bodyText}>
          <FormattedText
            id="confirmation.lateCancel.blocked.windowClosed"
            values={{ date: <strong>{copy.cutoffLabel}</strong> }}
          />
        </Typography>
      )}
      <PolicyTextBlock text={copy.policyText} />
      <ContactCard copy={copy} />
      <StillComing {...props} />
    </div>
  )
}

function LateDoneStep({ event, copy }: PolicyStepSectionProps) {
  return (
    <div className={classes.root} data-testid="late-cancel-done">
      <div className={classNames(classes.badge, classes.doneBadge)}>
        <Icon name="checkmark" />
      </div>
      <Typography variant="h1" className={classes.title}>
        <FormattedText id="confirmation.lateCancel.done.title" />
      </Typography>
      <div className={classes.details}>
        <ConfirmationEventDetails event={event} struckDate />
      </div>
      <Typography variant="body" className={classes.bodyText}>
        <FormattedText id="confirmation.lateCancel.done.body" values={{ business: copy.business }} />
      </Typography>
      <PolicyTextBlock text={copy.policyText} />
    </div>
  )
}

export function CancellationPolicyStep({ step, ...rest }: CancellationPolicyStepProps) {
  const intl = useIntl()
  const copy = resolvePolicyStepCopy(rest.event, intl.formatMessage({ id: 'confirmation.lateCancel.businessFallback' }))
  const sectionProps: PolicyStepSectionProps = { ...rest, submittingAction: rest.submittingAction ?? null, copy }

  if (step === 'warning') return <WarningStep {...sectionProps} />
  if (step === 'blocked') return <BlockedStep {...sectionProps} />
  return <LateDoneStep {...sectionProps} />
}
